/**
 * Step 3 — Review & publish. Summarises the tree, lists what still needs
 * fixing, then publishes with a live build log (campaign → each ad group →
 * its ads). Resumable: entities already created on TikTok are locked in the
 * draft and skipped, so a mid-way rejection can be fixed and re-run.
 * Everything is created paused; Activate flips the tree on afterwards.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, CheckCircle2, Circle, Film, Layers, Loader2, Rocket, XCircle } from 'lucide-react';
import { useTiktokWorkspace } from '@/contexts/TiktokWorkspaceContext';
import { Button } from '@/components/Button';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import { goalSpec } from '@/lib/tiktok-matrix';
import { ensureAdGroupSaved, ensureAdSaved, ensureCampaignSaved } from './saveHelpers';
import { treeIssues } from './validation';
import { OBJECTIVE_LABELS, alreadyToasted, groupForm, type Identity } from './workspaceShared';

type P = 'idle' | 'running' | 'done' | 'failed';

const BUDGET_LABEL: Record<string, string> = {
  BUDGET_MODE_DAY: 'daily', BUDGET_MODE_TOTAL: 'lifetime', BUDGET_MODE_INFINITE: 'no campaign cap',
};

function Section({ title, hint, right, children }: { title: string; hint?: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface shadow-soft">
      <div className="flex items-center gap-3 border-b border-border px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-primary">{title}</div>
          {hint && <div className="mt-0.5 text-xs text-text-muted">{hint}</div>}
        </div>
        {right}
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

const StepIcon = ({ s }: { s?: P }) =>
  s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
  : s === 'running' ? <Loader2 size={15} className="shrink-0 animate-spin text-primary" />
  : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
  : <Circle size={15} className="shrink-0 text-text-muted/40" />;

export function ReviewPublishPanel({ identities, onEditCampaign, onEditGroups, onActivate, onFocusAd }: {
  identities: Identity[];
  onEditCampaign: () => void;
  onEditGroups: () => void;
  /** Opens the activate dialog (existing ActivateDialog → activateTree). */
  onActivate: () => void;
  onFocusAd: (groupId: string, adId: string) => void;
}) {
  const navigate = useNavigate();
  const { campaign, adGroups, lockCampaign, lockAdGroup, lockAd, canPublish, reset } = useTiktokWorkspace();
  const { currency } = useTiktokAccountCurrency();

  const cf = campaign.formData as Record<string, string>;
  const objective = cf.objectiveType || campaign.objective || undefined;
  const issues = useMemo(() => treeIssues(campaign, adGroups, identities), [campaign, adGroups, identities]);
  const allIssues = [...issues.campaign, ...issues.groups];

  const [pub, setPub] = useState<Record<string, P>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const statusOf = (key: string, saved: boolean): P => pub[key] ?? (saved ? 'done' : 'idle');

  const publish = useCallback(async () => {
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: P) => setPub((m) => ({ ...m, [k]: v }));
    const bail = (k: string, prefix: string, r: { error: string; fromApi: boolean }) => {
      mark(k, 'failed');
      setPubError(`${prefix}${r.error}`);
      if (!r.fromApi) toast.error(r.error);
    };
    try {
      mark('campaign', 'running');
      const c = await ensureCampaignSaved(campaign, lockCampaign);
      if (!c.ok) { bail('campaign', '', c); return; }
      mark('campaign', 'done');

      for (const g of adGroups) {
        mark(g.id, 'running');
        const gr = await ensureAdGroupSaved(c.apiId, g, lockAdGroup, objective);
        if (!gr.ok) { bail(g.id, `${g.name}: `, gr); return; }
        mark(g.id, 'done');

        for (const a of g.ads) {
          mark(a.id, 'running');
          const ar = await ensureAdSaved(gr.apiId, g, a, identities, lockAd, objective);
          if (!ar.ok) { bail(a.id, `${g.name} → ${a.name}: `, ar); return; }
          mark(a.id, 'done');
        }
      }
      toast.success('Campaign and ads saved to TikTok. Review their status before activating.');
    } catch (e) {
      // Helpers catch their own API errors; this is a safety net.
      setPubError((e as Error)?.message ?? 'Publish failed.');
      if (!alreadyToasted(e)) toast.error('Publish failed.');
    } finally {
      setPublishing(false);
    }
  }, [campaign, adGroups, identities, objective, lockCampaign, lockAdGroup, lockAd]);

  const allSaved = campaign.isLocked && adGroups.length > 0
    && adGroups.every((g) => g.isLocked && g.ads.length > 0 && g.ads.every((a) => a.isLocked));

  const budgetLine = cf.budgetMode === 'BUDGET_MODE_INFINITE'
    ? 'No campaign budget (set per ad group)'
    : `${currency ?? ''} ${cf.budget || '—'} ${BUDGET_LABEL[cf.budgetMode ?? 'BUDGET_MODE_DAY'] ?? ''}`.trim();

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Section
          title={campaign.isLocked ? 'Campaign (on TikTok)' : 'Campaign'}
          right={!campaign.isLocked && <button type="button" onClick={onEditCampaign} className="h-7 rounded-md px-2 text-[11px] font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary">Edit</button>}
        >
          <dl className="space-y-2">
            {([
              ['Name', campaign.name || '—'],
              ['Objective', objective ? (OBJECTIVE_LABELS[objective] ?? objective) : '—'],
              ['Budget', budgetLine],
              ['Start', (cf.operationStatus ?? 'DISABLE') === 'ENABLE' ? 'Campaign enabled' : 'Paused'],
              ...(campaign.apiId ? [['TikTok id', campaign.apiId] as [string, string]] : []),
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex gap-3">
                <dt className="w-24 shrink-0 text-xs text-text-muted">{k}</dt>
                <dd className="min-w-0 flex-1 break-words text-[13px] text-text-primary">{v}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section
          title="Structure"
          right={<button type="button" onClick={onEditGroups} className="h-7 rounded-md px-2 text-[11px] font-medium text-text-secondary hover:bg-surface-alt hover:text-text-primary">Edit</button>}
        >
          <div className="space-y-2.5">
            {adGroups.length === 0 && <p className="text-xs text-text-muted">No ad groups yet.</p>}
            {adGroups.map((g) => {
              const gf = groupForm(g) as Record<string, string | string[]>;
              const goal = goalSpec(objective, (gf.optimizationGoal as string) ?? '');
              const locs = Array.isArray(gf.locationIds) ? (gf.locationIds as string[]) : [];
              return (
                <div key={g.id} className="rounded-lg border border-border p-2.5">
                  <div className="flex items-center gap-2">
                    <Layers size={13} className="shrink-0 text-text-muted" />
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name}</span>
                    <span className="shrink-0 text-[11px] text-text-muted">{g.ads.length} ad{g.ads.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="mt-1 pl-5 text-[11px] text-text-muted">
                    {locs.length} location{locs.length === 1 ? '' : 's'} · {currency ?? ''} {(gf.budget as string) || '—'} {BUDGET_LABEL[(gf.budgetMode as string) ?? 'BUDGET_MODE_DAY']}
                    {goal ? ` · ${goal.label} (${goal.billingEvent})` : ''}
                  </div>
                  <div className="mt-1.5 space-y-0.5 pl-5">
                    {g.ads.map((a) => (
                      <button key={a.id} type="button" onClick={() => onFocusAd(g.id, a.id)} className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-surface-alt">
                        <Film size={11} className="shrink-0 text-text-muted" />
                        <span className="min-w-0 flex-1 truncate text-[12px] text-text-primary">{a.name}</span>
                        {a.isLocked && <span className="shrink-0 text-[10px] text-success">on TikTok</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {allIssues.length > 0 && (
        <Section title={`${allIssues.length} thing${allIssues.length === 1 ? '' : 's'} to fix before publishing`}>
          <ul className="space-y-1.5">
            {allIssues.map((i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-danger"><XCircle size={13} className="mt-0.5 shrink-0" /> {i}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Publish" hint="Creates everything on TikTok in order. Ad groups and ads start paused unless you chose otherwise; the campaign is activated separately.">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5 text-sm text-text-primary">
            <StepIcon s={statusOf('campaign', campaign.isLocked)} />
            <span className="truncate">Campaign — {campaign.name || 'untitled'}</span>
            {campaign.isLocked && <span className="text-[11px] text-text-muted">already on TikTok</span>}
          </div>
          {adGroups.map((g) => (
            <div key={g.id} className="space-y-1">
              <div className="flex items-center gap-2.5 pl-5 text-sm text-text-primary">
                <StepIcon s={statusOf(g.id, g.isLocked)} />
                <span className="truncate">Ad group — {g.name}</span>
                {g.isLocked && <span className="text-[11px] text-text-muted">already on TikTok</span>}
              </div>
              {g.ads.map((a) => (
                <div key={a.id} className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                  <StepIcon s={statusOf(a.id, a.isLocked)} />
                  <span className="truncate">Ad — {a.name}</span>
                  {a.isLocked && <span className="text-[11px] text-text-muted">already on TikTok</span>}
                </div>
              ))}
            </div>
          ))}
        </div>

        {pubError && (
          <div className="mt-4 rounded-xl border border-danger/30 bg-danger-soft p-3">
            <p className="break-words text-xs text-danger">TikTok said: {pubError}</p>
            <p className="mt-1 text-[11px] text-text-muted">Fix it and publish again — anything already created is kept, not repeated.</p>
          </div>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate('/tiktok/campaigns')} disabled={publishing}>
            {allSaved ? 'Keep paused & close' : 'Cancel'}
          </Button>
          {!allSaved && (
            <Button onClick={publish} loading={publishing} disabled={publishing || allIssues.length > 0} iconLeft={<Check size={15} />}>
              {pubError ? 'Retry publish' : campaign.isLocked ? 'Publish remaining' : 'Publish campaign'}
            </Button>
          )}
          {canPublish && (
            <Button variant="primary" onClick={onActivate} disabled={publishing} iconLeft={<Rocket size={14} />}>
              Activate campaign
            </Button>
          )}
        </div>
        {allSaved && (
          <p className="mt-2 text-right text-2xs text-text-muted">
            Everything is saved on TikTok. Activate switches the campaign, ad groups and ads to ENABLE —
            or <button type="button" className="underline" onClick={() => { reset(); navigate('/tiktok/campaigns'); }}>finish and clear this draft</button>.
          </p>
        )}
      </Section>
    </>
  );
}
